import { type MouseEventHandler, type PointerEventHandler } from 'react'
import styled, { css } from 'styled-components'

import {
  PinnableTooltip,
  PinnableTooltipAnchor,
} from '@/components/global/PinnableTooltip/PinnableTooltip.styled'
import { TooltipWrapper } from '@/components/global/Tooltip/Tooltip.styled'
import { Icon } from '@/components/priority/Icon/Icon'
import { type ResizeCorner } from '@/utils/editor/resizeHotspot'
import { colors } from '@/utils/styling/colors'
import { zLayers } from '@/utils/styling/zLayers'

// =============================================================================
// LOW-RESOLUTION WARNING (icon in the warning bar with an interactive tooltip)
// =============================================================================

/**
 * The low-resolution tooltip, drawn to the right of its warning icon the way
 * the design places it. It opens and stays open the way every
 * `PinnableTooltip` does; a `::before` strip covers the 4px gap between icon
 * and tooltip so crossing it never counts as leaving the anchor.
 */
export const LowResTooltip = styled(PinnableTooltip)`
  top: 0;
  left: calc(100% + 4px);
  width: 175px;
  box-sizing: border-box;
  padding: 12px;
  background-color: #ffffff;
  border: 1px solid ${colors('gray4')};
  border-radius: 4px;
  box-shadow: 0 2px 10px 2px rgba(0, 0, 0, 0.05);
  color: ${colors('gray1')};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  overflow-wrap: break-word;
  pointer-events: auto;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -5px;
    width: 5px;
  }
`

/** Anchor for the warning icon and its tooltip; see `LowResTooltip`. */
export const LowResAnchor = styled(PinnableTooltipAnchor)`
  position: relative;
  display: flex;
`

/** The warning icon as a button, so a click can pin the tooltip open. */
export const LowResIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  margin: 0;
  border: 0;
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors('brand')};
    outline-offset: 1px;
  }
`

/** The "Enhance image size" link inside the tooltip, styled as inline text. */
export const LowResEnhanceLink = styled.button`
  display: inline;
  padding: 0;
  margin: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  color: ${colors('brand')};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    filter: brightness(1.15);
  }

  &:focus-visible {
    outline: 2px solid ${colors('brand')};
    outline-offset: 1px;
  }
`

// =============================================================================
// ITEM WRAPPER
// =============================================================================

type ItemWrapperProps = {
  $x: number
  $y: number
  $width: number
  $height: number
  $rotate?: number
}

/**
 * The geometry rides inline style (`.attrs`) rather than the styled template:
 * interpolating props that change on every resize frame makes
 * styled-components generate a new class and inject a new <style> per frame,
 * and the resulting style recalc makes the whole editor chrome judder.
 */
export const ItemWrapper = styled.div.attrs(
  ({ $x, $y, $width, $height, $rotate }: ItemWrapperProps) => ({
    style: {
      width: `${$width}px`,
      height: `${$height}px`,
      transform: `translate(${$x}px, ${$y}px)${
        $rotate ? ` rotate(${$rotate}deg)` : ''
      }`,
    },
  }),
)<ItemWrapperProps>`
  position: absolute;
  pointer-events: none;
  z-index: ${zLayers.itemControlOverlay};

  /* Elevate this wrapper when a tooltip inside is open (hovered, focused
     or pinned), so the tooltip renders above sibling overlay wrappers */
  &:has(${TooltipWrapper}:hover),
  &:has(${LowResAnchor}:hover),
  &:has(${LowResAnchor}:focus-within),
  &:has(${LowResAnchor}[data-pinned='true']) {
    z-index: ${zLayers.itemControlOverlay + 1};
  }
`

// =============================================================================
// SELECTION BORDER
// =============================================================================

type SelectionBorderProps = {
  $convertedToEditable?: boolean
  $editing?: boolean
}

/**
 * While editing, the outline steps 2px outward: at offset 0 it paints on the
 * box edge column, which is also the caret's column at a line start and a
 * line end, hiding the caret there.
 */
export const SelectionBorder = styled.div<SelectionBorderProps>(
  ({ $convertedToEditable, $editing }) => css`
    position: absolute;
    inset: 0;
    outline: 1px solid;
    outline-color: ${$convertedToEditable ? colors('orange') : colors('brand')};
    outline-offset: ${$editing ? '2px' : '0'};
    pointer-events: none;
  `,
)

// =============================================================================
// CONTROL TOOLBAR (horizontal bar above item)
// =============================================================================

type ControlToolbarProps = {
  $zoom: number
  $dockSide?: 'top' | 'bottom'
}

export const ControlToolbar = styled.div<ControlToolbarProps>(
  ({ $zoom, $dockSide }) => {
    const scale = Math.min(2, Math.max(0.5, 100 / $zoom))
    const gapScale = Math.min(2, Math.max(0.6, 100 / $zoom))
    const anchor =
      $dockSide === 'bottom'
        ? css`
            top: 100%;
            transform: translateX(-50%) translateY(${14 * gapScale}px)
              scale(${scale});
            transform-origin: top center;
          `
        : css`
            top: 0;
            transform: translateX(-50%)
              translateY(calc(-100% - ${6 * gapScale}px)) scale(${scale});
            transform-origin: bottom center;
          `
    return css`
      && {
        position: absolute;
        left: 50%;
        ${anchor}
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;
        padding: 4px;
        background-color: #ffffff;
        border-radius: 3px;
        box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
        pointer-events: auto;
        white-space: nowrap;
        z-index: 1;
      }
    `
  },
)

// =============================================================================
// WARNING BAR (vertical stack on right side)
// =============================================================================

type WarningBarProps = {
  $zoom: number
}

export const WarningBar = styled.div<WarningBarProps>(({ $zoom }) => {
  // Inverse scale to counteract page zoom, clamped to prevent extremes
  const scale = Math.min(2, Math.max(0.5, 100 / $zoom))
  return css`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    /* The bar overlays the item's face: it must never steal clicks meant for
       the content (double-click-to-edit). Only the icons themselves are
       hover/tooltip targets. */
    pointer-events: none;
    & > * {
      pointer-events: auto;
    }
    transform: translate(${-5 * scale}px, ${5 * scale}px) scale(${scale});
    transform-origin: top right;
  `
})

// =============================================================================
// HIDE IN PDF BADGE (top-left corner indicator)
// =============================================================================

type HideInPdfBadgeProps = {
  $zoom: number
}

export const HideInPdfBadge = styled.div<HideInPdfBadgeProps>(({ $zoom }) => {
  const scale = Math.min(2, Math.max(0.5, 100 / $zoom))
  return css`
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: auto;
    transform: translate(${5 * scale}px, ${5 * scale}px) scale(${scale});
    transform-origin: top left;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: #ffffff;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    /* Elevate above sibling WarningBar/ControlToolbar when tooltip is active */
    &:has(${TooltipWrapper}:hover) {
      z-index: 2;
    }
  `
})

// =============================================================================
// CORNER RESIZE HANDLES (all four corners)
// =============================================================================

type ResizeHitAreaProps = {
  $cursor: string
  $width: number
  $height: number
  $clipPath?: string
}

/**
 * `clip-path` clips hit-testing as well as paint, which is what confines the
 * grab to the corner band and lets a click inland fall through to the item.
 * The geometry rides inline style for the same reason as {@link ItemWrapper}.
 */
export const ResizeHitArea = styled.div.attrs(
  ({ $cursor, $width, $height, $clipPath }: ResizeHitAreaProps) => ({
    style: {
      width: `${$width}px`,
      height: `${$height}px`,
      cursor: `${$cursor}-resize`,
      ...($clipPath ? { clipPath: $clipPath } : {}),
    },
  }),
)<ResizeHitAreaProps>`
  pointer-events: auto;
`

export const ResizeCornerDot = styled.div<{ $corner: ResizeCorner }>(
  ({ $corner }) => css`
    position: absolute;
    ${$corner.includes('n') ? 'top: 1px;' : 'bottom: 1px;'}
    ${$corner.includes('w') ? 'left: 1px;' : 'right: 1px;'}
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #ffffff;
    box-shadow:
      0 0 0 1px ${colors('brand', 60)},
      0 0 2px rgba(0, 0, 0, 0.15);
    pointer-events: none;

    div:has(${ResizeHitArea}:active) > & {
      background: #2a8acd;
      box-shadow:
        0 0 0 1px #2a8acd,
        0 0 1px 1px #61ade4;
    }
  `,
)

// =============================================================================
// CONTROL ICONS (shared base styles for toolbar icons)
// =============================================================================

type ClickableIconProps = {
  onClick?: MouseEventHandler<HTMLDivElement>
  onMouseDown?: MouseEventHandler<HTMLDivElement>
  onPointerDown?: PointerEventHandler<HTMLDivElement>
  onPointerUp?: PointerEventHandler<HTMLDivElement>
  onPointerCancel?: PointerEventHandler<HTMLDivElement>
}

/**
 * The icon size is a variable so one toolbar icon serves both chromes: the
 * editor bar takes the defaults, the classic bar
 * (`classic/ClassicItemControlOverlay.styled.ts`) sets its own.
 */
const iconBase = css`
  cursor: pointer;
  background-color: transparent;
  border-radius: 4px;
  width: var(--overlay-icon-size, 24px);
  height: var(--overlay-icon-size, 24px);
  padding: var(--overlay-icon-padding, 4px);
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${colors('grey', 10)};
  }
`

export const DragHandleIcon = styled(Icon)<ClickableIconProps>`
  ${iconBase}
  cursor: move;
`

export const DuplicateIcon = styled(Icon)<ClickableIconProps>`
  ${iconBase}
`

export const DeleteIcon = styled(Icon)<ClickableIconProps>`
  ${iconBase}
`

/**
 * The lock icon reads as a control only where a tap flips the lock
 * (`$flips`); elsewhere it is a state readout with no hover.
 */
export const LockIcon = styled(Icon)<ClickableIconProps & { $flips?: boolean }>(
  ({ $flips }) => css`
    ${iconBase}
    cursor: ${$flips ? 'pointer' : 'default'};

    &:hover {
      background-color: ${$flips ? colors('grey', 10) : 'transparent'};
    }
  `,
)

export const SwapIcon = styled(Icon)<ClickableIconProps>`
  ${iconBase}
`

/**
 * A toolbar action drawn as a button around its icon, so the keyboard can
 * reach it and a screen reader can name it; it sizes and highlights like the
 * icons beside it.
 */
export const ToolbarIconButton = styled.button`
  ${iconBase}
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  border: 0;

  &:focus-visible {
    outline: 2px solid ${colors('brand')};
    outline-offset: 1px;
  }
`

type QuickActionsIconProps = ClickableIconProps & {
  $disabled?: boolean
}

export const QuickActionsIcon = styled(Icon)<QuickActionsIconProps>(
  ({ $disabled }) => css`
    ${iconBase}
    cursor: ${$disabled ? 'not-allowed' : 'pointer'};
    opacity: ${$disabled ? 0.5 : 1};

    &:hover {
      background-color: ${$disabled ? 'transparent' : colors('grey', 10)};
    }
  `,
)

export const ConvertToTextIcon = styled(Icon)<ClickableIconProps>`
  ${iconBase}
`

export const ServiceTemplateIcon = styled(Icon)<ClickableIconProps>`
  ${iconBase}
`

export const SwapTemplateIcon = styled(Icon)<ClickableIconProps>`
  ${iconBase}
`

/**
 * Held (`$held`) while a click would convert at once but another Save is
 * writing the line.
 */
export const EditorTextBoxConvertIcon = styled(Icon)<
  ClickableIconProps & { $held?: boolean }
>(
  ({ $held }) => css`
    ${iconBase}
    cursor: ${$held ? 'not-allowed' : 'pointer'};
    opacity: ${$held ? 0.5 : 1};

    &:hover {
      background-color: ${$held ? 'transparent' : colors('grey', 10)};
    }
  `,
)

// =============================================================================
// QUICK ACTIONS MENU CONTAINER (dropdown from toolbar)
// =============================================================================

export const QuickActionsMenuContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 4px;
  pointer-events: auto;
  z-index: 1;
`

// =============================================================================
// EDGE RESIZE HANDLES (single-axis pills)
// =============================================================================

const edgeHandleBase = css`
  position: absolute;
  background-color: #ffffff;
  border-radius: 3px;
  box-shadow:
    0 0 0 1px ${colors('brand', 60)},
    0 0 2px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
  transition: background-color 0.15s ease;

  &:hover,
  &:active {
    background-color: #2a8acd;
  }
`

/**
 * Right edge handle - drag horizontally to change width only
 */
export const RightEdgeHandle = styled.div`
  ${edgeHandleBase}
  width: 5px;
  height: 18px;
  right: -3px;
  top: 50%;
  transform: translateY(-50%);
  cursor: ew-resize;
`

/**
 * Left edge handle - drag horizontally to change width only, anchored right
 */
export const LeftEdgeHandle = styled.div`
  ${edgeHandleBase}
  width: 5px;
  height: 18px;
  left: -3px;
  top: 50%;
  transform: translateY(-50%);
  cursor: ew-resize;
`

/**
 * Top edge handle - drag vertically to change the saved height, anchored bottom
 */
export const TopEdgeHandle = styled.div`
  ${edgeHandleBase}
  width: 18px;
  height: 5px;
  top: -3px;
  left: 50%;
  transform: translateX(-50%);
  cursor: ns-resize;
`

type BottomEdgeHandleProps = {
  $disabled?: boolean
}

/**
 * Bottom edge handle - drag vertically to change height only
 * Disabled when height auto-adjusts (maintainAspectRatio is OFF)
 */
export const BottomEdgeHandle = styled.div<BottomEdgeHandleProps>(
  ({ $disabled }) => css`
    ${edgeHandleBase}
    width: 18px;
    height: 5px;
    bottom: -3px;
    left: 50%;
    transform: translateX(-50%);
    cursor: ${$disabled ? 'not-allowed' : 'ns-resize'};
    opacity: ${$disabled ? 0.4 : 1};

    &:hover,
    &:active {
      background-color: ${$disabled ? '#ffffff' : '#2a8acd'};
    }
  `,
)
